import React, { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'react-bootstrap';
import { TextField } from '@mui/material';
import _debounce from 'lodash/debounce';
import usePagination from '../../../../shared/hooks/usePagination';
import { InfluencerInvoiceService } from '../service';
import { Campaign } from '../../../../model/campaign/Campaign';
import { ErrorToast } from '../../../../utils/toasters';
import { Pagination } from '../../../../shared/components/Pagination';
import EmptyState from '../../../../shared/components/placeholder/EmptyState';
import InvoiceMainCard from '../components/InvoiceMainCard';
import InvoiceArchiveHeader, { IArchiveHeader } from './components/InvoiceArchiveHeader';
import { useInvoices } from '../../../../shared/hooks/useInvoices';
import { InfluencerOfferModal } from '../../offer/modal/offer-modal/InfluencerOfferModal';
import { InfluencerContractModal } from '../../offer/modal/contract-modal/InfluencerContractModal';
import ModalUploadInvoice from '../components/ModalUploadInvoice';
import { ConfirmationModal } from '../../../../shared/components/modal/ConfirmationModal';
import InvoiceSubCard from '../components/InvoiceSubCard';
import InvoiceSubCardSkeleton from '../components/InvoiceSubCardSkeleton';

const InvoiceArchive = () => {
    const { filterParams, handlePages, setFilterParams, setTotalPages, setCount, totalPages } = usePagination({
        perPage: 10,
    });
    const [showOfferModal, setShowOfferModal] = useState(false);
    const [showContractModal, setShowContractModal] = useState(false);

    const [expandedCampaignIds, setExpandedCampaignIds] = useState<Set<number>>(new Set());

    const [archiveCampaigns, setArchiveCampaigns] = useState<Campaign[]>([]);
    const [selectedCampaignDetails, setSelectedCampaignDetails] = useState({
        isLongTermOffer: false,
        campaignId: 0,
        title: '',
    });
    const [invoicesTotals, setInvoicesTotals] = useState<IArchiveHeader>({
        totalEarnings: 0,
        outstandingEarnings: 0,
        activeCampaigns: 0,
        completedCampaigns: 0,
    });
    const { t } = useTranslation();
    const {
        uploadModalData,
        setUploadModalData,
        showDeleteModal,
        setShowDeleteModal,
        handleSubmitUpload,
        deleteInvoice,
        downloadInvoice,
        isUploading,
        isDeleting,
    } = useInvoices({
        t,
        filterParams,
        setFilterParams,
        isArchivedInvoice: true,
    });

    useEffect(() => {
        const getInvoiceArchive = async () => {
            InfluencerInvoiceService.getInvoiceArchive({
                ...filterParams,
            })
                .then(response => {
                    setArchiveCampaigns(response.data);
                    setCount(response.count);
                    setInvoicesTotals(response.info);
                    setTotalPages(Math.ceil(response.count / response.perPage));
                })
                .catch(error => ErrorToast(error));
        };
        getInvoiceArchive();
    }, [filterParams]);

    const changeFilterHandler = _debounce((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFilterParams({ ...filterParams, [name]: value, page: 1 });
    }, 400);

    const openUploadModal = (
        invoiceId: number,
        sequenceNumber: number,
        file?: string,
        rejectReason?: string,
        isLongTermOffer?: boolean,
        selectedCampaignId?: number,
        numberInvoices?: number,
        campaignTitle?: string
    ) => {
        setUploadModalData(prevState => ({
            ...prevState,
            show: true,
            sequenceNumber,
            invoiceId,
            invoiceFile: file,
            rejectReason: rejectReason,
            isLongTermOffer,
            selectedCampaignId,
            invoiceLength: numberInvoices,
            campaignTitle,
        }));
    };

    const openModalAction = (
        isContractModal: boolean,
        isLongTermOffer?: boolean,
        campaignId?: number,
        title?: string
    ) => {
        isContractModal ? setShowContractModal(true) : setShowOfferModal(true);
        setSelectedCampaignDetails(prevState => ({
            ...prevState,
            isLongTermOffer: isLongTermOffer ?? false,
            campaignId: campaignId ?? 0,
            title: title ?? '',
        }));
    };

    const toggleExpanded = (id: number) => {
        setExpandedCampaignIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    return (
        <>
            <InfluencerOfferModal
                show={showOfferModal}
                setShowModal={setShowOfferModal}
                campaignId={String(selectedCampaignDetails?.campaignId ?? 0)}
                isLongTerm={selectedCampaignDetails.isLongTermOffer}
            />
            <InfluencerContractModal
                show={showContractModal}
                setShowModal={setShowContractModal}
                campaignId={String(selectedCampaignDetails.campaignId)}
            />
            <ModalUploadInvoice
                closeModal={() =>
                    setUploadModalData({
                        show: false,
                        rejectReason: undefined,
                        sequenceNumber: undefined,
                        invoiceId: undefined,
                        invoiceFile: undefined,
                    })
                }
                rejectReason={uploadModalData?.rejectReason}
                invoiceFile={uploadModalData?.invoiceFile}
                show={uploadModalData.show}
                handleSubmit={handleSubmitUpload}
                isLoading={isUploading}
                sequenceNumber={uploadModalData.sequenceNumber}
                invoiceLength={uploadModalData.invoiceLength}
                campaignTitle={uploadModalData.campaignTitle}
            />
            <ConfirmationModal
                title={t('influencer.invoice.modal.title')}
                description={t('influencer.invoice.modal.desc', {
                    campaignName: showDeleteModal?.title,
                })}
                action={deleteInvoice}
                show={showDeleteModal.show}
                closeModal={() => setShowDeleteModal(prev => ({ ...prev, show: false, title: '' }))}
                isLoading={isDeleting}
            />

            <Row className={`${totalPages === 1 && 'mb-5'}`}>
                <InvoiceArchiveHeader invoicesTotals={invoicesTotals} />
                <h6>{t('influencer.invoice.invoice_overview')}</h6>
                <Col sm={6} lg={4} xl={3} className="mb-2">
                    <TextField
                        label={`${t('general.button.search')}`}
                        variant="outlined"
                        name="title"
                        onChange={changeFilterHandler}
                        size="small"
                        fullWidth
                    />
                </Col>

                {archiveCampaigns.length ? (
                    archiveCampaigns?.map(campaign => {
                        const isExpanded = expandedCampaignIds.has(campaign?.id ?? 0);
                        return (
                            <React.Fragment key={campaign?.offers?.[0]?.id ?? campaign?.id}>
                                <Col className="col-12 mt-4" onClick={() => toggleExpanded(campaign.id)}>
                                    <InvoiceMainCard
                                        selectedCampaign={campaign}
                                        downloadInvoice={downloadInvoice}
                                        openUploadModal={openUploadModal}
                                        openDeleteModal={setShowDeleteModal}
                                        openModalAction={openModalAction}
                                        expended={isExpanded}
                                    />
                                </Col>

                                {isExpanded &&
                                    campaign?.offers?.[0]?.invoiceSchedules?.map((invoice, _i, arr) =>
                                        invoice?.isAllowed ? (
                                            <Col xs={12} key={invoice.id}>
                                                <InvoiceSubCard
                                                    openUploadModal={openUploadModal}
                                                    invoice={invoice}
                                                    length={arr.length}
                                                    openDeleteModal={setShowDeleteModal}
                                                    downloadInvoice={downloadInvoice}
                                                    selectedCampaignId={campaign?.id}
                                                    selectedCampaignTitle={campaign?.title}
                                                />
                                            </Col>
                                        ) : (
                                            <Col xs={12} key={invoice.id}>
                                                <InvoiceSubCardSkeleton
                                                    length={arr.length}
                                                    dueDate={invoice?.dueDate}
                                                    sequenceNumber={invoice?.sequenceNumber}
                                                />
                                            </Col>
                                        )
                                    )}
                            </React.Fragment>
                        );
                    })
                ) : (
                    <EmptyState
                        desc={t('influencer.invoice.empty_desc')}
                        addClass="col-12 text-center mt-5"
                        title={t('influencer.invoice.empty_title')}
                        iconPath="/icons/profile/invoice.svg"
                        btnText={t('influencer.invoice.chose_campaign')}
                        navigateTo={'/influencer/invoices'}
                        variant="primary"
                    />
                )}
                {totalPages > 1 && (
                    <div className="d-flex justify-content-end mb-md-4">
                        <Pagination page={filterParams.page} totalPages={totalPages} handlePagination={handlePages} />
                    </div>
                )}
            </Row>
        </>
    );
};

export default InvoiceArchive;
